/**
 * Authentication Manager
 * Handles SSO (Google), email/password authentication, two-step account
 * creation (name → credentials), email verification, and password reset.
 *
 * Screen navigation: every state corresponds to a sibling `.auth-screen`
 * container inside `#login-overlay`. `showScreen(name)` toggles visibility.
 */

import { API_URL, API } from './core/api.js';

const SCREENS = [
  'ssoLanding',
  'signInScreen',
  'betaAccessScreen',
  'createStep1Screen',
  'createStep2Screen',
  'verificationScreen',
  'forgotPasswordScreen',
  'passwordResetScreen',
  'consentScreen',
];

export class AuthManager {
  constructor(app) {
    this.app = app;
    this.initialized = false;

    // Landing + SSO buttons
    this.googleSignInBtn = null;
    this.emailSignInBtn = null;
    this.tryFreeBtn = null;
    this.ssoDivider = null;

    // Beta access gate
    this.betaAccessForm = null;
    this.betaAccessCode = null;
    this.betaAccessMessage = null;
    this.betaAccessActions = null;
    this.betaGoogleSignupBtn = null;
    this.betaEmailSignupBtn = null;
    this.betaTryFreeBtn = null;

    // Sign-in form
    this.loginForm = null;
    this.loginMessage = null;
    this.forgotPasswordLink = null;

    // Create-account forms
    this.createStep1Form = null;
    this.createStep1Message = null;
    this.createStep2Form = null;
    this.registerMessage = null;

    // Verification
    this.verificationEmail = null;
    this.verificationCode = null;
    this.verifyCodeBtn = null;
    this.resendCodeBtn = null;
    this.verificationTimer = null;
    this.verificationMessage = null;

    // Password reset
    this.forgotPasswordForm = null;
    this.forgotPasswordEmail = null;
    this.forgotPasswordMessage = null;
    this.passwordResetForm = null;
    this.newPasswordInput = null;
    this.confirmPasswordInput = null;
    this.passwordResetMessage = null;

    // Consent screen (Partner B linkage consent)
    this.consentTitle = null;
    this.consentInviterName = null;
    this.consentAcceptBtn = null;
    this.consentDeclineBtn = null;
    this.consentMessage = null;
    this.consentBillingLine = null;
    this.pendingInviteToken = null;

    // State
    this.intent = null;
    this.currentScreen = null;
    this.pendingFirstName = null;
    this.verificationId = null;
    this.countdownInterval = null;
    this.resetToken = null;
    this.betaAuthorized = false;

    this.providers = { local: true, google: false };
  }

  async initialize() {
    if (this.initialized) return;

    await this.loadProviders();

    this.googleSignInBtn = document.getElementById('googleSignInBtn');
    this.emailSignInBtn = document.getElementById('emailSignInBtn');
    this.tryFreeBtn = document.getElementById('tryFreeBtn');
    this.ssoDivider = document.getElementById('ssoDivider');

    this.betaAccessForm = document.getElementById('betaAccessForm');
    this.betaAccessCode = document.getElementById('betaAccessCode');
    this.betaAccessMessage = document.getElementById('betaAccessMessage');
    this.betaAccessActions = document.getElementById('betaAccessActions');
    this.betaGoogleSignupBtn = document.getElementById('betaGoogleSignupBtn');
    this.betaEmailSignupBtn = document.getElementById('betaEmailSignupBtn');
    this.betaTryFreeBtn = document.getElementById('betaTryFreeBtn');

    this.loginForm = document.getElementById('loginForm');
    this.loginMessage = document.getElementById('loginMessage');
    this.forgotPasswordLink = document.getElementById('forgotPasswordLink');

    this.createStep1Form = document.getElementById('createStep1Form');
    this.createStep1Message = document.getElementById('createStep1Message');
    this.createStep2Form = document.getElementById('createStep2Form');
    this.registerMessage = document.getElementById('registerMessage');

    this.verificationEmail = document.getElementById('verificationEmail');
    this.verificationCode = document.getElementById('verificationCode');
    this.verifyCodeBtn = document.getElementById('verifyCodeBtn');
    this.resendCodeBtn = document.getElementById('resendCodeBtn');
    this.verificationTimer = document.getElementById('verificationTimer');
    this.verificationMessage = document.getElementById('verificationMessage');

    this.forgotPasswordForm = document.getElementById('forgotPasswordForm');
    this.forgotPasswordEmail = document.getElementById('forgotPasswordEmail');
    this.forgotPasswordMessage = document.getElementById('forgotPasswordMessage');
    this.passwordResetForm = document.getElementById('passwordResetForm');
    this.newPasswordInput = document.getElementById('newPasswordInput');
    this.confirmPasswordInput = document.getElementById('confirmPasswordInput');
    this.passwordResetMessage = document.getElementById('passwordResetMessage');

    this.consentTitle = document.getElementById('consentTitle');
    this.consentInviterName = document.getElementById('consentInviterName');
    this.consentAcceptBtn = document.getElementById('consentAcceptBtn');
    this.consentDeclineBtn = document.getElementById('consentDeclineBtn');
    this.consentMessage = document.getElementById('consentMessage');
    this.consentBillingLine = document.getElementById('consentBillingLine');

    this._applyIntentCopy();
    this.attachEventListeners();
    await this.refreshBetaAccessStatus();
    this.updateProviderButtons();
    this.checkForResetToken();
    if (!this.currentScreen) {
      this.showScreen(this.intent === 'signup' && !this.betaAuthorized ? 'betaAccessScreen' : 'ssoLanding');
    }

    this.initialized = true;
  }

  _applyIntentCopy() {
    this.intent = new URLSearchParams(window.location.search).get('intent')
      || (window.location.pathname === '/signup' ? 'signup' : null);

    const title = document.getElementById('ssoTitle');
    const subtext = document.getElementById('ssoSubtext');
    if (!title || !subtext) return;

    const copy = {
      signin: {
        title: 'Welcome back',
        subtext: 'Sign in to continue your relationship journey.',
      },
      signup: {
        title: 'Start your journey',
        subtext: 'Create an account, or sign in to get started.',
      },
    }[this.intent];

    if (copy) {
      title.textContent = copy.title;
      subtext.textContent = copy.subtext;
    }
  }

  async loadProviders() {
    try {
      const result = await API.getAuthProviders();
      if (result?.providers) this.providers = result.providers;
    } catch (error) {
      console.error('Failed to load auth providers:', error);
    }
  }

  updateProviderButtons() {
    if (this.googleSignInBtn) {
      this.googleSignInBtn.style.display = this.providers.google ? 'flex' : 'none';
    }
    if (this.betaGoogleSignupBtn) {
      this.betaGoogleSignupBtn.style.display = this.providers.google ? 'flex' : 'none';
    }
    this.updateBetaAccessUI();
  }

  async refreshBetaAccessStatus() {
    const status = await API.getBetaAccessStatus();
    this.betaAuthorized = !!(status?.authorized || this.app?._betaAuthorized);
    this.updateBetaAccessUI();
  }

  /**
   * True when the overlay is being shown over a guest who is already inside the
   * app — which is only ever a conversion, since there is no other reason to
   * raise it over a live guest session. Derived from app state rather than
   * stored, so it cannot go stale across reopens of the one long-lived
   * AuthManager instance.
   *
   * Two things key off it:
   * - "Try it free" is the overlay's *cancel*, not an admission request, so it
   *   must stay visible and clickable no matter what `betaAuthorized` says. A
   *   fresh AuthManager never has admission (it lives in the server session, on
   *   a 1-hour TTL), and hiding it strands them on the overlay.
   * - "Continue with Email" must open registration, not sign-in: a converting
   *   guest by definition has no account yet.
   * @returns {boolean}
   */
  get _isConvertingActiveGuest() {
    return this.app?.initialized === true && this.app?.isGuest === true;
  }

  updateBetaAccessUI() {
    // The divider exists to separate the SSO buttons from "Try it free", so it
    // hides on exactly the same condition — never on `betaAuthorized` alone.
    // A converting guest keeps the button (it is their cancel, see
    // `_isConvertingActiveGuest`), so they must keep the divider too. Before
    // this, an un-admitted visitor saw an "or" separating the buttons from
    // nothing, under copy promising an account-creation action below it.
    const showTryFree = this.betaAuthorized || this._isConvertingActiveGuest;
    if (this.tryFreeBtn) {
      this.tryFreeBtn.hidden = !showTryFree;
    }
    if (this.ssoDivider) {
      this.ssoDivider.hidden = !showTryFree;
    }
    if (this.betaAccessActions) this.betaAccessActions.hidden = !this.betaAuthorized;
    const submit = this.betaAccessForm?.querySelector('button[type="submit"]');
    if (submit) submit.hidden = this.betaAuthorized;
    if (this.betaAccessCode) this.betaAccessCode.disabled = this.betaAuthorized;
  }

  attachEventListeners() {
    // Welcome screen
    this.googleSignInBtn?.addEventListener('click', () => this.handleGoogleSignIn());
    this.emailSignInBtn?.addEventListener('click', () => this.handleEmailSignInClick());
    this.tryFreeBtn?.addEventListener('click', () => this.handleTryFree());
    this.betaAccessForm?.addEventListener('submit', (e) => this.handleBetaAccessSubmit(e));
    this.betaGoogleSignupBtn?.addEventListener('click', () => this.handleGoogleSignIn());
    this.betaEmailSignupBtn?.addEventListener('click', () => this.handleCreateAccountNavigation());
    this.betaTryFreeBtn?.addEventListener('click', () => this.handleTryFree());

    // Sign-in form
    this.loginForm?.addEventListener('submit', (e) => this.handleLogin(e));
    this.forgotPasswordLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this.showScreen('forgotPasswordScreen');
    });

    // Create account
    this.createStep1Form?.addEventListener('submit', (e) => this.handleCreateStep1Submit(e));
    this.createStep2Form?.addEventListener('submit', (e) => this.handleCreateStep2Submit(e));

    // Verification
    this.verifyCodeBtn?.addEventListener('click', () => this.handleVerifyCode());
    this.resendCodeBtn?.addEventListener('click', () => this.handleResendCode());
    this.verificationCode?.addEventListener('input', (e) => this.formatCodeInput(e));
    this.verificationCode?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleVerifyCode();
      }
    });

    // Password reset
    this.forgotPasswordForm?.addEventListener('submit', (e) => this.handleForgotPassword(e));
    this.passwordResetForm?.addEventListener('submit', (e) => this.handlePasswordReset(e));

    // Consent (scaffold — real wiring to /api/couples/invitations later)
    this.consentAcceptBtn?.addEventListener('click', () => this.handleConsentAccept());
    this.consentDeclineBtn?.addEventListener('click', () => this.handleConsentDecline());

    // Shared: back buttons and footer navigation
    document.querySelectorAll('[data-back-target]').forEach((btn) => {
      btn.addEventListener('click', () => this.showScreen(btn.dataset.backTarget));
    });
    document.querySelectorAll('[data-nav-target]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        if (link.dataset.navTarget === 'betaAccessScreen' || link.dataset.navTarget === 'createStep1Screen') {
          this.handleCreateAccountNavigation();
        } else {
          this.showScreen(link.dataset.navTarget);
        }
      });
    });

    // Shared: password show/hide toggles
    document.querySelectorAll('[data-toggle-password]').forEach((btn) => {
      btn.addEventListener('click', () => this.togglePasswordVisibility(btn));
    });
  }

  // ===========================================
  // SCREEN NAVIGATION
  // ===========================================

  showScreen(name) {
    if (!SCREENS.includes(name)) return;

    const ANIM_CLASSES = [
      'auth-screen--entering',
      'auth-screen--leaving',
      'sso-landing--entering',
      'sso-landing--leaving',
    ];

    // Snap any in-flight transition to its final state before starting a new one.
    SCREENS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const wasLeaving = el.classList.contains('auth-screen--leaving')
        || el.classList.contains('sso-landing--leaving');
      el.classList.remove(...ANIM_CLASSES);
      if (wasLeaving) el.hidden = true;
    });

    if (this.currentScreen === name) return;

    this.clearMessages();
    if (name !== 'verificationScreen') this.clearCountdown();

    const prevName = this.currentScreen;
    const prevEl = prevName ? document.getElementById(prevName) : null;
    const newEl = document.getElementById(name);
    if (!newEl) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldAnimate = !!prevEl && !prevEl.hidden && !prefersReduced;

    if (!shouldAnimate) {
      SCREENS.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.hidden = id !== name;
      });
    } else {
      // Hide screens that aren't part of this transition.
      SCREENS.forEach((id) => {
        if (id === name || id === prevName) return;
        const el = document.getElementById(id);
        if (el) el.hidden = true;
      });

      const enterClass = name === 'ssoLanding' ? 'sso-landing--entering' : 'auth-screen--entering';
      const leaveClass = prevName === 'ssoLanding' ? 'sso-landing--leaving' : 'auth-screen--leaving';

      newEl.hidden = false;
      newEl.classList.add(enterClass);
      prevEl.classList.add(leaveClass);

      const onLeaveEnd = (e) => {
        if (e.target !== prevEl) return;
        prevEl.removeEventListener('animationend', onLeaveEnd);
        prevEl.classList.remove(...ANIM_CLASSES);
        prevEl.hidden = true;
      };
      const onEnterEnd = (e) => {
        if (e.target !== newEl) return;
        newEl.removeEventListener('animationend', onEnterEnd);
        newEl.classList.remove(...ANIM_CLASSES);
      };
      prevEl.addEventListener('animationend', onLeaveEnd);
      newEl.addEventListener('animationend', onEnterEnd);
    }

    this.currentScreen = name;

    // Guests only: signing in attaches them to a DIFFERENT existing account and
    // abandons the guest row. Every other route off this overlay carries their
    // work forward, so this is the one place that needs saying out loud.
    const guestNote = document.getElementById('signInGuestNote');
    if (guestNote) {
      guestNote.hidden = !(name === 'signInScreen' && this.app?.isGuest === true);
    }

    const focusTargets = {
      signInScreen: 'loginEmail',
      betaAccessScreen: 'betaAccessCode',
      createStep1Screen: 'registerFirstName',
      createStep2Screen: 'registerEmail',
      verificationScreen: 'verificationCode',
      forgotPasswordScreen: 'forgotPasswordEmail',
      passwordResetScreen: 'newPasswordInput',
      consentScreen: 'consentTitle',
    };
    const targetId = focusTargets[name];
    if (targetId) {
      setTimeout(() => document.getElementById(targetId)?.focus(), shouldAnimate ? 420 : 100);
    }
  }

  /**
   * "Continue with Email" on the landing screen. Signup is signalled two ways:
   * `?intent=signup` for links that arrive cold (marketing and partner-invite
   * CTAs), and an active guest session for the in-app conversion gate, which
   * opens the overlay in place and so has no URL to carry the intent.
   */
  handleEmailSignInClick() {
    if (this.intent === 'signup' || this._isConvertingActiveGuest) {
      this.handleCreateAccountNavigation();
    } else {
      this.showScreen('signInScreen');
    }
  }

  handleCreateAccountNavigation() {
    this.showScreen(this.betaAuthorized ? 'createStep1Screen' : 'betaAccessScreen');
  }

  showBetaAccessPrompt(message) {
    this.betaAuthorized = false;
    this.updateBetaAccessUI();
    this.showScreen('betaAccessScreen');
    this.showError(message, this.betaAccessMessage);
  }

  /**
   * Re-title the overlay's landing screen for a conversion moment, and relabel
   * "Try it free" as the decline it now is. Applied after `initialize()` so it
   * wins over `_applyIntentCopy`'s URL-driven copy.
   *
   * @param {{title: string, subtext: string, cancelLabel?: string}} copy
   */
  applyConversionCopy(copy) {
    if (!copy) return;
    const title = document.getElementById('ssoTitle');
    const subtext = document.getElementById('ssoSubtext');
    if (title) title.textContent = copy.title;
    if (subtext) subtext.textContent = copy.subtext;

    const cancelSpan = this.tryFreeBtn?.querySelector('span');
    if (cancelSpan && copy.cancelLabel) cancelSpan.textContent = copy.cancelLabel;

    this.updateBetaAccessUI();
    this.showScreen('ssoLanding');
  }

  async handleBetaAccessSubmit(event) {
    event.preventDefault();
    const code = this.betaAccessCode?.value.trim();
    if (!code) {
      this.showError('Enter your beta access code.', this.betaAccessMessage);
      return;
    }

    const submit = this.betaAccessForm?.querySelector('button[type="submit"]');
    if (submit) {
      submit.disabled = true;
      submit.textContent = 'Validating…';
    }
    try {
      const result = await API.validateBetaAccess(code);
      if (!result?.authorized) {
        this.showError(result?.error || 'That beta access code is not valid.', this.betaAccessMessage);
        return;
      }
      this.betaAuthorized = true;
      this.app._betaAuthorized = true;
      this.updateBetaAccessUI();
      this.showSuccess('Access confirmed. Choose how you would like to get started.', this.betaAccessMessage);
    } catch (error) {
      console.error('Beta access validation error:', error);
      this.showError('Connection error. Please try again.', this.betaAccessMessage);
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.textContent = 'Validate access code';
      }
    }
  }

  togglePasswordVisibility(toggleBtn) {
    const input = document.getElementById(toggleBtn.dataset.togglePassword);
    if (!input) return;
    const isPassword = input.type === 'password';
    input.type = isPassword ? 'text' : 'password';
    toggleBtn.textContent = isPassword ? 'Hide' : 'Show';
    toggleBtn.setAttribute('aria-label', isPassword ? 'Hide password' : 'Show password');
  }

  // ===========================================
  // CONSENT SCREEN (Partner B linkage — scaffold)
  // ===========================================

  /**
   * Show the partner-linkage consent screen.
   *
   * @param {Object} [options]
   * @param {string} [options.inviterName='your partner']
   * @param {string|null} [options.inviterPhoto]
   * @param {string} [options.token] - Invitation token (required for accept)
   * @param {boolean} [options.partnerRateAvailable=false] - Whether the partner
   *   rate is configured, from the auth gate payload. `true` surfaces the
   *   neutral billing line; anything else (including a payload that omits the
   *   field) hides it. Never discloses the inviter's plan.
   */
  showConsentScreen({
    inviterName = 'your partner',
    inviterPhoto = null,
    token = null,
    partnerRateAvailable = false
  } = {}) {
    const name = (inviterName || '').trim() || 'your partner';
    this.pendingInviteToken = token;

    if (this.consentInviterName) this.consentInviterName.textContent = name;
    document
      .querySelectorAll('.consent-disclosure__inviter-ref')
      .forEach((el) => { el.textContent = name; });

    // Photo slot — not yet added to DOM; placeholder for future avatar rendering.
    // Kept intentionally simple: if a photo is provided, surface it via a data
    // attribute the CSS (future) can consume, but don't fail if no slot exists.
    if (this.consentTitle) {
      if (inviterPhoto) {
        this.consentTitle.dataset.inviterPhoto = inviterPhoto;
      } else {
        delete this.consentTitle.dataset.inviterPhoto;
      }
    }

    // Billing line: only when the partner rate is offered at all.
    if (this.consentBillingLine) {
      this.consentBillingLine.hidden = partnerRateAvailable !== true;
    }

    this._clearConsentMessage();
    this._setConsentBusy(false);
    this.showScreen('consentScreen');
  }

  async handleConsentAccept() {
    if (!this.pendingInviteToken) {
      this._showConsentError('No pending invitation to accept. Please try the link again.');
      return;
    }
    this._clearConsentMessage();
    this._setConsentBusy(true);

    try {
      const res = await fetch(
        `${API_URL}/discovery/couples/invitations/${encodeURIComponent(this.pendingInviteToken)}/accept`,
        { method: 'POST', credentials: 'include' }
      );
      const json = await res.json().catch(() => null);

      if (!res.ok || json?.success === false) {
        const reason = json?.metadata?.reason;
        const message = json?.error || 'Something went wrong.';
        // Branch on structured reason for nicer UX; generic otherwise.
        if (reason === 'already_in_couple') {
          this._showConsentError(
            'You\'re already connected with another partner. To connect with this one, disconnect from your current partner in Settings first.'
          );
        } else if (reason === 'not_active') {
          this._showConsentError('This invitation is no longer active.');
        } else if (reason === 'cannot_self_accept') {
          this._showConsentError('You cannot accept your own invitation.');
        } else {
          this._showConsentError(message);
        }
        this._setConsentBusy(false);
        return;
      }

      this.pendingInviteToken = null;
      this.app.onLoginSuccess();
    } catch (err) {
      console.error('[Consent] accept error:', err);
      this._showConsentError('Something went wrong. Please try again.');
      this._setConsentBusy(false);
    }
  }

  async handleConsentDecline() {
    this._clearConsentMessage();
    this._setConsentBusy(true);
    try {
      await fetch(`${API_URL}/discovery/couples/invitations/session/decline`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      // Decline is purely a session clear — a network failure still
      // shouldn't block the user from entering the app.
      console.warn('[Consent] decline request failed (non-fatal):', err?.message);
    }
    this.pendingInviteToken = null;
    this.app.onLoginSuccess();
  }

  _setConsentBusy(busy) {
    if (this.consentAcceptBtn) this.consentAcceptBtn.disabled = !!busy;
    if (this.consentDeclineBtn) this.consentDeclineBtn.disabled = !!busy;
    if (this.consentAcceptBtn) {
      this.consentAcceptBtn.textContent = busy ? 'Connecting…' : 'Connect and continue';
    }
  }

  _showConsentError(message) {
    if (!this.consentMessage) return;
    this.consentMessage.textContent = message;
    this.consentMessage.className = 'form-message error';
    this.consentMessage.style.display = 'block';
  }

  _clearConsentMessage() {
    if (!this.consentMessage) return;
    this.consentMessage.textContent = '';
    this.consentMessage.className = 'form-message';
    this.consentMessage.style.display = 'none';
  }

  // ===========================================
  // SSO HANDLERS
  // ===========================================

  handleGoogleSignIn() {
    if (this.googleSignInBtn) {
      this.googleSignInBtn.disabled = true;
      this.googleSignInBtn.querySelector('span').textContent = 'Redirecting...';
    }
    window.location.href = `${API_URL}/auth/google`;
  }

  async handleTryFree() {
    // For a guest who is already inside the app, this button is the conversion
    // overlay's *cancel* — it returns them to the session they were admitted to.
    // Gating that on `betaAuthorized` (which a freshly-constructed AuthManager
    // never has, since admission lives in the server session) bounced them to
    // the beta-code screen instead, removing the overlay's only way out.
    if (!this.betaAuthorized && !this._isConvertingActiveGuest) {
      this.showBetaAccessPrompt('Enter a beta access code before starting a guest session.');
      return;
    }
    if (this.tryFreeBtn) {
      this.tryFreeBtn.disabled = true;
      this.tryFreeBtn.querySelector('span').textContent = 'Starting...';
    }

    try {
      await this.app.initializeAsGuest();
    } catch (error) {
      console.error('[AuthManager] Guest initialization failed:', error);
      if (this.tryFreeBtn) {
        this.tryFreeBtn.disabled = false;
        this.tryFreeBtn.querySelector('span').textContent = 'Try it free - no signup required';
      }
    }
  }

  // ===========================================
  // SIGN IN
  // ===========================================

  async handleLogin(event) {
    event.preventDefault();
    this.clearMessages();

    const formData = new FormData(this.loginForm);
    const email = formData.get('email')?.trim().toLowerCase();
    const password = formData.get('password');

    if (!email || !this.validateEmail(email)) {
      this.showError('Please enter a valid email address', this.loginMessage);
      return;
    }
    if (!password || password.length < 6) {
      this.showError('Password must be at least 6 characters', this.loginMessage);
      return;
    }

    const submitButton = this.loginForm.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = 'Signing in...';

    try {
      const result = await API.login(email, password);

      if (result === null) {
        this.showError('Connection error. Please try again.', this.loginMessage);
      } else if (result.user) {
        this.showSuccess('Login successful!', this.loginMessage);
        this.app.currentUser = result.user;
        if (result.hasPendingInvite) {
          this.showConsentScreen({
            inviterName: result.pendingInviteInviter?.firstName,
            inviterPhoto: result.pendingInviteInviter?.photoUrl,
            token: result.pendingInviteToken,
            partnerRateAvailable: result.partnerRateAvailable
          });
        } else {
          this.app.onLoginSuccess();
        }
        return;
      } else if (result.code === 'SSO_ACCOUNT') {
        const provider = result.metadata?.provider || 'SSO';
        this.showError(
          `This account uses ${provider} sign-in. Please use the "${provider}" button instead.`,
          this.loginMessage
        );
      } else if (result.code === 'EMAIL_NOT_VERIFIED') {
        this.showError('Please verify your email before logging in.', this.loginMessage);
      } else {
        this.showError(result.error || 'Login failed', this.loginMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      this.showError('Connection error. Please try again.', this.loginMessage);
    }

    submitButton.disabled = false;
    submitButton.textContent = originalText;
  }

  // ===========================================
  // CREATE ACCOUNT
  // ===========================================

  handleCreateStep1Submit(event) {
    event.preventDefault();
    this.clearMessages();

    const formData = new FormData(this.createStep1Form);
    const firstName = formData.get('firstName')?.trim();

    if (!firstName) {
      this.showError('Please enter your first name', this.createStep1Message);
      return;
    }

    this.pendingFirstName = firstName;
    this.showScreen('createStep2Screen');
  }

  async handleCreateStep2Submit(event) {
    event.preventDefault();
    this.clearMessages();

    const formData = new FormData(this.createStep2Form);
    const email = formData.get('email')?.trim().toLowerCase();
    const password = formData.get('password');

    if (!email || !this.validateEmail(email)) {
      this.showError('Please enter a valid email address', this.registerMessage);
      return;
    }
    if (!password || password.length < 8) {
      this.showError('Password must be at least 8 characters', this.registerMessage);
      return;
    }
    if (!this.pendingFirstName) {
      this.showError('Please start again from step 1.', this.registerMessage);
      setTimeout(() => this.showScreen('createStep1Screen'), 1500);
      return;
    }

    const submitButton = this.createStep2Form.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = 'Sending code...';

    try {
      const result = await API.registerInitiate({
        email,
        password,
        firstName: this.pendingFirstName,
      });

      if (result === null) {
        this.showError('Connection error. Please try again.', this.registerMessage);
      } else if (result.verificationId) {
        this.verificationId = result.verificationId;
        this.showScreen('verificationScreen');
        if (this.verificationEmail) this.verificationEmail.textContent = result.maskedEmail;
        if (this.verificationCode) this.verificationCode.value = '';
        this.startCountdown(result.expiresIn);
      } else {
        if (result.code?.startsWith('BETA_')) {
          this.showBetaAccessPrompt(result.error || 'Your beta access is no longer valid.');
          return;
        }
        this.showError(result.error || 'Failed to send verification code', this.registerMessage);
      }
    } catch (error) {
      console.error('Registration initiate error:', error);
      this.showError('Connection error. Please try again.', this.registerMessage);
    }

    submitButton.disabled = false;
    submitButton.textContent = originalText;
  }

  async handleVerifyCode() {
    this.clearMessages();

    const code = this.verificationCode?.value.replace(/\s/g, '');
    if (!code || code.length !== 6) {
      this.showError('Please enter the complete 6-digit code', this.verificationMessage);
      return;
    }

    this.verifyCodeBtn.disabled = true;
    this.verifyCodeBtn.textContent = 'Verifying...';

    try {
      const result = await API.registerVerify({ verificationId: this.verificationId, code });

      if (result === null) {
        this.showError('Connection error. Please try again.', this.verificationMessage);
      } else if (result.user) {
        this.clearCountdown();
        this.showSuccess('Account created successfully!', this.verificationMessage);
        this.app.currentUser = result.user;

        // Whether this registration converted a guest is a fact about the user
        // we HAD, not about which button opened the overlay — the server
        // converts in place either way. Keying off the call-site flag
        // (`_isConvertingGuestToAccount`) missed every route that reaches
        // registration without passing through showLoginOverlayForConversion,
        // notably the sidebar's own "Sign in": those users ran onLoginSuccess
        // instead, which re-runs initialize() (duplicating modules and
        // listeners) and leaves the app still believing it is a guest.
        if (this.app.isGuest) {
          // Emits auth:account-created itself, with fromConversion: true.
          await this.app.handleGuestConversion(result.user);
        } else {
          // This response is the only moment a cold signup is distinguishable
          // from a sign-in: both branches below end at onLoginSuccess(), which
          // returning users reach too. Record it here, once, before the split —
          // the invite branch reaches onLoginSuccess only after consent, and the
          // account already exists by then.
          this.app.noteAccountCreated({ user: result.user, method: 'email' });

          if (result.hasPendingInvite) {
            this.showConsentScreen({
              inviterName: result.pendingInviteInviter?.firstName,
              inviterPhoto: result.pendingInviteInviter?.photoUrl,
              token: result.pendingInviteToken,
              partnerRateAvailable: result.partnerRateAvailable
            });
          } else {
            this.app.onLoginSuccess();
          }
        }
        return;
      } else {
        if (result.code?.startsWith('BETA_')) {
          this.showBetaAccessPrompt(result.error || 'Your beta access is no longer valid.');
          return;
        }
        const remaining = result.metadata?.attemptsRemaining;
        if (remaining !== undefined && remaining >= 0) {
          this.showError(
            `Invalid code. ${remaining} ${remaining === 1 ? 'attempt' : 'attempts'} remaining.`,
            this.verificationMessage
          );
        } else {
          this.showError(result.error || 'Verification failed', this.verificationMessage);
        }

        if (result.code === 'VERIFICATION_EXPIRED' || result.code === 'VERIFICATION_TOO_MANY_ATTEMPTS') {
          setTimeout(() => this.showScreen('createStep2Screen'), 2000);
        }
      }
    } catch (error) {
      console.error('Verification error:', error);
      this.showError('Connection error. Please try again.', this.verificationMessage);
    }

    this.verifyCodeBtn.disabled = false;
    this.verifyCodeBtn.textContent = 'Verify & create account';
  }

  async handleResendCode() {
    this.clearMessages();

    this.resendCodeBtn.disabled = true;
    this.resendCodeBtn.textContent = 'Sending...';

    try {
      const result = await API.registerResend({ verificationId: this.verificationId });

      if (result === null) {
        this.showError('Connection error. Please try again.', this.verificationMessage);
      } else if (result.remainingResends !== undefined) {
        this.showSuccess(
          `New code sent. ${result.remainingResends} ${result.remainingResends === 1 ? 'resend' : 'resends'} remaining.`,
          this.verificationMessage
        );
        this.startCountdown(15 * 60);

        if (result.remainingResends === 0) {
          this.resendCodeBtn.disabled = true;
          this.resendCodeBtn.textContent = 'No resends remaining';
          return;
        }
      } else {
        this.showError(result.error || 'Failed to resend code', this.verificationMessage);
        if (result.code === 'VERIFICATION_EXPIRED') {
          setTimeout(() => this.showScreen('createStep2Screen'), 2000);
        }
      }
    } catch (error) {
      console.error('Resend error:', error);
      this.showError('Connection error. Please try again.', this.verificationMessage);
    }

    this.resendCodeBtn.disabled = false;
    this.resendCodeBtn.textContent = "Didn't receive it? Resend code";
  }

  // ===========================================
  // PASSWORD RESET
  // ===========================================

  checkForResetToken() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token && window.location.pathname === '/reset-password') {
      this.resetToken = token;
      this.showScreen('passwordResetScreen');
    }
  }

  async handleForgotPassword(event) {
    event.preventDefault();

    const email = this.forgotPasswordEmail?.value.trim().toLowerCase();
    if (!email || !this.validateEmail(email)) {
      this.showError('Please enter a valid email address', this.forgotPasswordMessage);
      return;
    }

    const submitBtn = this.forgotPasswordForm?.querySelector('button[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending...';
    }

    try {
      const result = await API.passwordForgot(email);
      if (result) {
        this.showSuccess(
          'If an account exists with this email, you will receive a reset link shortly.',
          this.forgotPasswordMessage
        );
      } else {
        this.showError('An error occurred. Please try again.', this.forgotPasswordMessage);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      this.showError('An error occurred. Please try again.', this.forgotPasswordMessage);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }

  async handlePasswordReset(event) {
    event.preventDefault();

    const newPassword = this.newPasswordInput?.value;
    const confirmPassword = this.confirmPasswordInput?.value;

    if (!newPassword || newPassword.length < 8) {
      this.showError('Password must be at least 8 characters', this.passwordResetMessage);
      return;
    }
    if (newPassword !== confirmPassword) {
      this.showError('Passwords do not match', this.passwordResetMessage);
      return;
    }

    const submitBtn = this.passwordResetForm?.querySelector('button[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Resetting...';
    }

    try {
      const result = await API.passwordReset(this.resetToken, newPassword);

      if (result && !result.error) {
        this.showSuccess(
          'Password reset successfully! Redirecting to sign in...',
          this.passwordResetMessage
        );
        window.history.replaceState({}, document.title, '/');
        setTimeout(() => {
          this.resetToken = null;
          this.showScreen('signInScreen');
        }, 2000);
      } else {
        const errorMsg = result?.error || 'Failed to reset password. Please request a new link.';
        // A dead token cannot be retried on this form — the only way forward is
        // a new email, so surface that as a control. Other failures (weak
        // password, server error) are retryable here and get the plain message.
        const deadToken = result?.code === 'RESET_INVALID_TOKEN'
          || result?.code === 'RESET_TOKEN_EXPIRED';
        if (deadToken) {
          this.showErrorWithAction(errorMsg, this.passwordResetMessage, {
            label: 'Request a new link',
            onClick: () => {
              this.resetToken = null;
              window.history.replaceState({}, document.title, '/');
              this.showScreen('forgotPasswordScreen');
            }
          });
        } else {
          this.showError(errorMsg, this.passwordResetMessage);
        }
      }
    } catch (error) {
      console.error('Password reset error:', error);
      this.showError('An error occurred. Please try again.', this.passwordResetMessage);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }

  // ===========================================
  // COUNTDOWN TIMER
  // ===========================================

  startCountdown(seconds) {
    this.clearCountdown();

    let remaining = seconds;
    const updateDisplay = () => {
      const minutes = Math.floor(remaining / 60);
      const secs = remaining % 60;
      if (this.verificationTimer) {
        this.verificationTimer.textContent = `Code expires in ${minutes}:${secs.toString().padStart(2, '0')}`;
        this.verificationTimer.classList.remove('expired');
      }
    };
    updateDisplay();

    this.countdownInterval = setInterval(() => {
      remaining--;
      updateDisplay();
      if (remaining <= 0) {
        this.clearCountdown();
        if (this.verificationTimer) {
          this.verificationTimer.textContent = 'Code expired';
          this.verificationTimer.classList.add('expired');
        }
        this.showError('Verification code expired. Please request a new one.', this.verificationMessage);
      }
    }, 1000);
  }

  clearCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  // ===========================================
  // UTILITIES
  // ===========================================

  validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  formatCodeInput(e) {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 6) value = value.slice(0, 6);
    if (value.length > 3) value = value.slice(0, 3) + ' ' + value.slice(3);
    e.target.value = value;
  }

  showError(message, container) {
    if (!container) return;
    container.textContent = message;
    container.className = 'form-message error';
    container.style.display = 'block';
  }

  /**
   * `showError` plus a trailing action control, for errors whose recovery is a
   * different screen rather than a retry of the form the user is looking at.
   *
   * The invalid/expired reset link is the case it exists for: the "Reset
   * password" button sits directly above the message, but it re-submits the
   * same dead token, so the copy ("request a new one") pointed at no usable
   * control. `showError` writes `textContent`, which cannot carry a link.
   *
   * The message and the action are wrapped in one element so the flex row set
   * up by `.auth-screen .form-message` stays two children — the `::before`
   * status glyph and the body.
   *
   * @param {string} message
   * @param {HTMLElement} container
   * @param {{label: string, onClick: () => void}} action
   */
  showErrorWithAction(message, container, action) {
    if (!container) return;
    container.textContent = '';

    const body = document.createElement('span');
    body.className = 'form-message__body';
    body.textContent = `${message} `;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'form-message__action';
    btn.textContent = action.label;
    btn.addEventListener('click', action.onClick);

    body.appendChild(btn);
    container.appendChild(body);
    container.className = 'form-message error';
    container.style.display = 'block';
  }

  showSuccess(message, container) {
    if (!container) return;
    container.textContent = message;
    container.className = 'form-message success';
    container.style.display = 'block';
  }

  clearMessages() {
    [
      this.loginMessage,
      this.betaAccessMessage,
      this.createStep1Message,
      this.registerMessage,
      this.verificationMessage,
      this.forgotPasswordMessage,
      this.passwordResetMessage,
      this.consentMessage,
    ].forEach((el) => {
      if (el) {
        el.textContent = '';
        el.className = 'form-message';
        el.style.display = 'none';
      }
    });
  }
}
